import React, {
  createRef,
  FunctionComponent,
  useMemo,
  useState,
  useEffect,
  useRef,
} from 'react';
import { Button, Form, Card } from 'react-bootstrap';
import { Redirect } from 'react-router-dom';
import { connect, ConnectedProps } from 'react-redux';
import { ErrorModal } from '../components';
import { AuthDispatches, ErrorDispatches } from '../redux/Dispatches';
import { ApplicationState } from '../redux/States';
import loginLogo from '../assets/images/login/logo01.png';

const mapState = (state: ApplicationState) => ({
  ...state.auth,
  ...state.error,
});
const mapDispatch = { ...AuthDispatches, ...ErrorDispatches };
const connector = connect(mapState, mapDispatch);
type Props = ConnectedProps<typeof connector>;

const ALLOWED = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789'; // 去除易混淆: I,O,0,1,L
const randomCode = (len: number) =>
  Array.from(
    { length: len },
    () => ALLOWED[Math.floor(Math.random() * ALLOWED.length)]
  ).join('');

const loginPage: FunctionComponent<Props> = ({
  isAuth,
  signIn,
  catchErrorForModal,
}) => {
  const qs = new URLSearchParams(window.location.search);
  const lastPath = qs.get('lastPath');
  if (lastPath && isAuth) return <Redirect to={lastPath} />;

  const syncRef = createRef<HTMLInputElement>();
  const accountRef = createRef<HTMLInputElement>();
  const pRef = createRef<HTMLInputElement>();

  const [showP, setShowP] = useState(false);

  // ===== 純前端圖片驗證碼（內嵌版） =====
  const [captchaSeed, setCaptchaSeed] = useState(0); // 變更就重繪
  const [captchaCode, setCaptchaCode] = useState(''); // 當前正確碼
  const [captchaInput, setCaptchaInput] = useState(''); // 使用者輸入
  const [captchaErr, setCaptchaErr] = useState('');
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const genCode = useMemo(() => randomCode(5), [captchaSeed]); // 長度可調
  useEffect(() => {
    setCaptchaCode(genCode);
  }, [genCode]);

  const drawNoise = (ctx: CanvasRenderingContext2D, w: number, h: number) => {
    // 干擾線
    const lines = 3 + Math.floor(Math.random() * 2);
    ctx.strokeStyle = '#7f8c8d';
    ctx.fillStyle = '#95a5a6';
    for (let i = 0; i < lines; i++) {
      ctx.beginPath();
      ctx.moveTo(Math.random() * w, Math.random() * h);
      ctx.bezierCurveTo(
        Math.random() * w,
        Math.random() * h,
        Math.random() * w,
        Math.random() * h,
        Math.random() * w,
        Math.random() * h
      );
      ctx.lineWidth = 1;
      ctx.globalAlpha = 0.6;
      ctx.stroke();
    }
    // 雜點
    const dots = 30;
    for (let i = 0; i < dots; i++) {
      ctx.beginPath();
      ctx.arc(Math.random() * w, Math.random() * h, 1, 0, Math.PI * 2);
      ctx.globalAlpha = 0.6;
      ctx.fill();
    }
    ctx.globalAlpha = 1;
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const width = 140;
    const height = 48;
    const ratio = window.devicePixelRatio || 1;
    canvas.width = width * ratio;
    canvas.height = height * ratio;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);

    // 背景
    ctx.clearRect(0, 0, width, height);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);

    // 文字
    const code = captchaCode || genCode;
    const per = width / (code.length + 1);
    for (let i = 0; i < code.length; i++) {
      const ch = code[i];
      const fontSize = 26 + Math.floor(Math.random() * 6);
      const angle = (Math.random() - 0.5) * 0.6; // 旋轉
      ctx.save();
      ctx.font = `${fontSize}px sans-serif`;
      ctx.textBaseline = 'middle';
      ctx.translate(per * (i + 0.9), height / 2);
      ctx.rotate(angle);
      ctx.fillStyle = '#000000';
      ctx.fillText(ch, -8, 2);
      ctx.restore();
    }

    drawNoise(ctx, width, height);
  }, [captchaCode, genCode]);

  const verifyCaptcha = () =>
    captchaInput.trim().toUpperCase() === captchaCode.toUpperCase();

  const doLogin = () => {
    setCaptchaErr('');
    if (!accountRef.current?.value || !pRef.current?.value) return;
    if (!verifyCaptcha()) {
      setCaptchaErr('驗證碼不正確，請重新輸入或點選重整。');
      setCaptchaSeed((s) => s + 1);
      setCaptchaInput('');
      return;
    }
    signIn(
      accountRef.current.value,
      pRef.current.value,
      syncRef.current?.checked || false
    ).catch(catchErrorForModal);
  };

  document.title = '學生健康資訊系統';

  return (
    <React.Fragment>
      <div className="auth-wrapper">
        <div className="auth-content">
          <div className="auth-bg">
            <span className="r" />
            <span className="r s" />
            <span className="r s" />
            <span className="r" />
          </div>
          <Card className="borderless text-center">
            <Card.Body>
              <img
                src={loginLogo}
                alt="icon"
                style={{ width: '250px', height: 'auto', marginBottom: '16px' }}
              />
              <Form.Group controlId="formAccount">
                <Form.Label>帳號</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="請輸入帳號"
                  ref={accountRef}
                  autoComplete="off"
                />
              </Form.Group>
              <Form.Group controlId="formPassword">
                <Form.Label>密碼</Form.Label>
                <div style={{ position: 'relative' }}>
                  <Form.Control
                    type={showP ? 'text' : 'password'}
                    placeholder="請輸入密碼"
                    ref={pRef}
                    style={{ paddingRight: '40px' }}
                  />
                  <i
                    className={`fas ${showP ? 'fa-eye-slash' : 'fa-eye'}`}
                    onClick={() => setShowP((v) => !v)}
                    style={{
                      position: 'absolute',
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      cursor: 'pointer',
                      color: '#6c757d',
                    }}
                    title={showP ? '隱藏密碼' : '顯示密碼'}
                  />
                </div>
              </Form.Group>

              {/* 圖片驗證碼（純前端） */}
              <Form.Group controlId="formCaptcha" className="mt-3">
                <Form.Label>圖片驗證碼</Form.Label>
                <div
                  className="d-flex align-items-center justify-content-center"
                  style={{ gap: 12 }}
                >
                  <canvas ref={canvasRef} aria-label="圖形驗證碼" role="img" />
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    type="button"
                    className="rounded-circle"
                    style={{
                      width: 26,
                      height: 26,
                      padding: 0,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      lineHeight: 1,
                    }}
                    onClick={() => {
                      setCaptchaSeed((s) => s + 1);
                      setCaptchaErr('');
                      setCaptchaInput('');
                    }}
                    aria-label="重整驗證碼"
                    title="重整驗證碼"
                  >
                    <i
                      className="fas fa-sync-alt fa-xs"
                      style={{ lineHeight: 1, margin: 0 }}
                    />
                  </Button>
                </div>
                <Form.Control
                  className="mt-2"
                  type="text"
                  placeholder="請輸入上方英數字(不區分大小寫)"
                  value={captchaInput}
                  onChange={(e) => {
                    const next = e.target.value
                      .replace(/[^a-z0-9]/gi, '')
                      .slice(0, 8);
                    setCaptchaInput(next);
                    if (captchaErr) setCaptchaErr('');
                  }}
                  autoComplete="off"
                />
                {captchaErr && (
                  <Form.Text className="text-danger">{captchaErr}</Form.Text>
                )}
              </Form.Group>

              <div className="text-center mb-3 d-flex justify-content-center mt-3">
                <Form.Check ref={syncRef} label />
                <Form.Label
                  style={{ cursor: 'pointer' }}
                  onClick={() => syncRef.current?.click()}
                >
                  從中央端更新帳號多學制權限
                </Form.Label>
              </div>

              <Button variant="primary" type="submit" onClick={doLogin}>
                登入
              </Button>
            </Card.Body>
          </Card>
        </div>
      </div>
      <ErrorModal />
    </React.Fragment>
  );
};

export const LoginPage = connector(loginPage);
